import type { Lang } from '@/features/survey/i18n';

/*
 * Formatting for the Minions dashboard, as the design writes it: French figures
 * on the French page ("168 884", "29,6 %", "2 min 26 s", "3 nov."), British
 * ones on the English page ("168,884", "29.6%", "3 Nov"). A missing figure is
 * "—", never 0. Units sit behind a no-break space so a value never wraps.
 */

const NBSP = '\u00a0';

const localeOf = (lang: Lang) => (lang === 'fr' ? 'fr-FR' : 'en-GB');

const isNum = (n: number | null | undefined): n is number => n != null && Number.isFinite(n);

export function makeFormat(lang: Lang) {
  const loc = localeOf(lang);
  const int = (n: number | null | undefined) => (isNum(n) ? new Intl.NumberFormat(loc, { maximumFractionDigits: 0 }).format(n) : '—');
  /** 4.6349 → "4,63" / "4.63" (one or two decimals). */
  const dec = (n: number | null | undefined) => (isNum(n) ? new Intl.NumberFormat(loc, { minimumFractionDigits: 1, maximumFractionDigits: 2 }).format(n) : '—');
  /** 29.63 → "29,6 %" / "29.6%". */
  const pct = (n: number | null | undefined) => {
    if (!isNum(n)) return '—';
    const s = new Intl.NumberFormat(loc, { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(n);
    return lang === 'fr' ? `${s}${NBSP}%` : `${s}%`;
  };
  /** Seconds → whole hours: "53 650 h". */
  const hours = (sec: number | null | undefined) => (isNum(sec) ? `${int(sec / 3600)}${NBSP}h` : '—');
  /** Seconds → "2 min 26 s", or "1 h 05 min" from an hour up. */
  const dur = (sec: number | null | undefined) => {
    if (!isNum(sec)) return '—';
    const s = Math.max(0, Math.round(sec));
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    return h ? `${h}${NBSP}h ${String(m).padStart(2, '0')}${NBSP}min` : `${m}${NBSP}min ${String(s % 60).padStart(2, '0')}${NBSP}s`;
  };
  /** Axis ticks: 60000 → "60 k" / "60K". */
  const compact = (n: number) => new Intl.NumberFormat(loc, { notation: 'compact', maximumFractionDigits: 1 }).format(n);
  /** A calendar day: "long" → "jeudi 26 novembre", "short" → "jeu. 26 nov.", "dayMonth" → "26 nov.". */
  const day = (iso: string, style: 'long' | 'short' | 'dayMonth') => {
    const d = new Date(`${iso}T12:00:00Z`);
    if (Number.isNaN(d.getTime())) return '—';
    const opts: Intl.DateTimeFormatOptions =
      style === 'long'
        ? { weekday: 'long', day: 'numeric', month: 'long' }
        : style === 'short'
          ? { weekday: 'short', day: 'numeric', month: 'short' }
          : { day: 'numeric', month: 'short' };
    return new Intl.DateTimeFormat(loc, { ...opts, timeZone: 'UTC' }).format(d);
  };
  /** Campaign window: "3 nov. → 6 déc. 2026" / "3 Nov → 6 Dec 2026" (both years when they differ). */
  const period = (start: string, end: string) => {
    const sameYear = start.slice(0, 4) === end.slice(0, 4);
    return `${day(start, 'dayMonth')}${sameYear ? '' : ` ${start.slice(0, 4)}`} → ${day(end, 'dayMonth')} ${end.slice(0, 4)}`;
  };
  return { lang, int, dec, pct, hours, dur, compact, day, period };
}

export type Fmt = ReturnType<typeof makeFormat>;

/** ISO instant → "14:05:12" in the campaign's timezone; null when there is none. */
export function fmtClock(iso: string | null | undefined, timeZone: string): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return new Intl.DateTimeFormat('fr-FR', { timeZone, hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false }).format(d);
}

/** Round "nice" ceiling for a bar axis on 1 / 2 / 5 × 10ⁿ steps. */
export function niceScale(max: number, target = 4): { max: number; step: number } {
  if (max <= 0) return { max: 1, step: 1 };
  const raw = max / target;
  const pow = 10 ** Math.floor(Math.log10(raw));
  const step = Math.max(1, [1, 2, 5, 10].map((m) => m * pow).find((s) => s >= raw) ?? 10 * pow);
  return { max: Math.ceil(max / step) * step, step };
}
